import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { deleteUser, getEmployeeReport, getUsers, resetUserPassword, updateUser } from '../utils/api';
import { openReportPreview } from '../utils/export';
import { fmt } from '../utils/format';

function Modal({ open, onClose, title, children, footer }) {
  if (!open) return null;
  return (
    <div className="modal-overlay" onClick={(event) => event.target === event.currentTarget && onClose()}>
      <div className="modal modal-sm scale-in">
        <div className="modal-head">
          <div className="modal-title">{title}</div>
          <button className="modal-close" onClick={onClose}>x</button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

export default function EmployeesPage() {
  const qc = useQueryClient();
  const [resetTarget, setResetTarget] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => getUsers().then((response) => response.data),
  });

  const { data: employeeReport = [] } = useQuery({
    queryKey: ['employee-report'],
    queryFn: () => getEmployeeReport().then((response) => response.data),
  });

  const reportById = useMemo(() => new Map(employeeReport.map((row) => [row.id, row])), [employeeReport]);

  const toggleMut = useMutation({
    mutationFn: ({ id, is_active }) => updateUser(id, { is_active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
    onError: (err) => toast.error(err?.response?.data?.error || 'Unable to update employee'),
  });

  const deleteMut = useMutation({
    mutationFn: (id) => deleteUser(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
    onError: (err) => toast.error(err?.response?.data?.error || 'Unable to deactivate employee'),
  });

  const resetMut = useMutation({
    mutationFn: ({ id, password }) => resetUserPassword(id, password),
    onSuccess: () => {
      setResetTarget(null);
      setNewPassword('');
      toast.success('Password reset');
    },
    onError: (err) => toast.error(err?.response?.data?.error || 'Unable to reset password'),
  });

  function exportEmployeePdf(user) {
    const metrics = reportById.get(user.id) || {};
    openReportPreview(
      `${user.full_name} Performance Report`,
      `Generated ${fmt.datetime(new Date())}`,
      [
        {
          type: 'cards',
          title: 'Summary',
          items: [
            { label: 'Role', value: user.role },
            { label: 'Transactions', value: fmt.number(metrics.tx) },
            { label: 'Revenue', value: fmt.currency(metrics.revenue) },
            { label: 'Average Basket', value: fmt.currency(metrics.avg_basket) },
          ],
        },
        {
          title: 'Employee Details',
          columns: ['Field', 'Value'],
          rows: [
            ['Name', user.full_name],
            ['Username', user.username],
            ['Email', user.email || '-'],
            ['Status', user.is_active ? 'Active' : 'Inactive'],
            ['Last Login', user.last_login ? fmt.datetime(user.last_login) : 'Never'],
          ],
        },
      ]
    );
  }

  return (
    <div className="p-page fade-up">
      <div className="inventory-toolbar mb-16">
        <div>
          <div className="card-title" style={{ fontSize: 18 }}>Employees</div>
          <div className="card-sub">Reset passwords, export PDF reports, and manage active staff from one place.</div>
        </div>
        <div className="inventory-actions">
          <button className="btn btn-secondary" onClick={() => qc.invalidateQueries({ queryKey: ['users'] })}>Refresh</button>
        </div>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table>
            <thead><tr><th>Employee</th><th>Role</th><th>Status</th><th>Transactions</th><th>Revenue</th><th>Actions</th></tr></thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan="6" style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan="6"><div className="empty-state"><div className="empty-title">No employees found</div></div></td></tr>
              ) : users.map((user) => {
                const metrics = reportById.get(user.id) || {};
                return (
                  <tr key={user.id}>
                    <td>
                      <div className="flex gap-8">
                        <div className={`avatar av-sm ${user.role === 'supervisor' ? 'av-teal' : user.role === 'admin' ? 'av-blue' : 'av-green'}`}>{user.avatar_initials || fmt.initials(user.full_name)}</div>
                        <div>
                          <div className="td-main">{user.full_name}</div>
                          <div className="text-xs text-muted">{user.username} {user.email ? `- ${user.email}` : ''}</div>
                        </div>
                      </div>
                    </td>
                    <td><span className={`badge ${user.role === 'supervisor' ? 'badge-teal' : user.role === 'admin' ? 'badge-blue' : 'badge-gray'}`} style={{ textTransform: 'capitalize' }}>{user.role}</span></td>
                    <td><span className={`badge ${user.is_active ? 'badge-green' : 'badge-gray'}`}>{user.is_active ? 'Active' : 'Inactive'}</span></td>
                    <td>{fmt.number(metrics.tx)}</td>
                    <td className="td-mono">{fmt.currency(metrics.revenue)}</td>
                    <td>
                      <div className="inventory-row-actions">
                        <button className="btn btn-secondary btn-sm" onClick={() => exportEmployeePdf(user)}>PDF Report</button>
                        <button className="btn btn-secondary btn-sm" onClick={() => { setResetTarget(user); setNewPassword(''); }}>Reset Password</button>
                        <button className="btn btn-ghost btn-sm" onClick={() => toggleMut.mutate({ id: user.id, is_active: !user.is_active })}>{user.is_active ? 'Disable' : 'Enable'}</button>
                        {user.role !== 'admin' && <button className="btn btn-danger btn-sm" onClick={() => deleteMut.mutate(user.id)}>Deactivate</button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        open={!!resetTarget}
        onClose={() => setResetTarget(null)}
        title={`Reset Password: ${resetTarget?.full_name || ''}`}
        footer={(
          <>
            <button className="btn btn-secondary" onClick={() => setResetTarget(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={() => resetMut.mutate({ id: resetTarget.id, password: newPassword })} disabled={resetMut.isPending || newPassword.length < 6}>Reset Password</button>
          </>
        )}
      >
        <div className="form-group">
          <label className="form-label">New Password</label>
          <input className="form-input" type="password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} placeholder="At least 6 characters" />
        </div>
        <div className="text-sm text-muted">The employee will use this password on their next login.</div>
      </Modal>
    </div>
  );
}
